import { useState } from "react";
import { site } from "../data/content";
import { Mail, Linkedin, Phone, ExternalLink } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch("https://formspree.io/f/mojrepez", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          organization: form.organization,
          message: form.message,
          _subject: `Enquiry from ${form.name} via reconsultx.com`,
        }),
      });
      if (res.ok) {
        setStatus("sent");
        setForm({ name: "", email: "", organization: "", message: "" });
      } else setStatus("error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="pt-28 pb-24">
      <section className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="overline">Contact</div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-4 leading-[1.02]" style={{ color: "var(--primary)" }}>
            Let&apos;s <span style={{ color: "var(--accent)", fontStyle: "italic" }}>talk</span>.
          </h1>
          <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Advisory engagements, speaking invitations, due-diligence, techno-commercial modelling, or an open conversation on decarbonization — I&apos;d love to hear from you.
          </p>

          <div className="mt-10 space-y-4">
            <a href={`mailto:${site.email}`} className="flex items-center gap-3 py-3 link-underline" data-testid="contact-email">
              <Mail size={16} style={{ color: "var(--accent-strong)" }} />
              <span>{site.email}</span>
            </a>
            <a href={`tel:${site.phone.replace(/\s+/g, "")}`} className="flex items-center gap-3 py-3 link-underline" data-testid="contact-phone">
              <Phone size={16} style={{ color: "var(--accent-strong)" }} />
              <span>{site.phone}</span>
            </a>
            <a href={site.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 py-3 link-underline" data-testid="contact-linkedin">
              <Linkedin size={16} style={{ color: "var(--accent-strong)" }} />
              <span className="inline-flex items-center gap-1">linkedin.com/in/himansshu-agrawal-renewables <ExternalLink size={12} /></span>
            </a>
          </div>
        </div>

        <form
          className="md:col-span-7 p-8 md:p-10 space-y-5"
          style={{ backgroundColor: "var(--surface)", border: "1px solid var(--border-soft)" }}
          onSubmit={onSubmit}
          data-testid="contact-form"
        >
          <div className="overline">Enquiry form</div>
          <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Send me a direct message — replies usually within 48 hours.
          </p>

          <Field label="Your name" required>
            <input required data-testid="form-name" value={form.name} onChange={onChange("name")}
              className="w-full bg-transparent py-3 px-4 outline-none text-base"
              style={{ border: "1px solid var(--border-soft)", color: "var(--text-primary)" }} />
          </Field>
          <Field label="Your email" required>
            <input required type="email" data-testid="form-email" value={form.email} onChange={onChange("email")}
              className="w-full bg-transparent py-3 px-4 outline-none text-base"
              style={{ border: "1px solid var(--border-soft)", color: "var(--text-primary)" }} />
          </Field>
          <Field label="Organization">
            <input data-testid="form-org" value={form.organization} onChange={onChange("organization")}
              className="w-full bg-transparent py-3 px-4 outline-none text-base"
              style={{ border: "1px solid var(--border-soft)", color: "var(--text-primary)" }} />
          </Field>
          <Field label="Message" required>
            <textarea required rows={5} data-testid="form-message" value={form.message} onChange={onChange("message")}
              className="w-full bg-transparent py-3 px-4 outline-none text-base resize-y"
              style={{ border: "1px solid var(--border-soft)", color: "var(--text-primary)" }} />
          </Field>

          <div className="flex flex-wrap items-center gap-4 pt-2">
            <button
              type="submit"
              data-testid="form-submit"
              disabled={status === "sending"}
              className="inline-flex items-center gap-2 px-7 py-3 text-sm transition-colors disabled:opacity-60"
              style={{ backgroundColor: "var(--primary)", color: "var(--bg)" }}
            >
              {status === "sending" ? "Sending…" : "Send message"} <Mail size={14} />
            </button>
            {status === "sent" && (
              <span className="text-xs font-semibold" style={{ color: "var(--accent-strong)" }}>
                ✓ Message sent — I&apos;ll reply soon.
              </span>
            )}
            {status === "error" && (
              <span className="text-xs" style={{ color: "#A33" }}>
                Something went wrong — please email {site.email}.
              </span>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="overline mb-2" style={{ color: "var(--text-secondary)" }}>{label}{required && " *"}</div>
      {children}
    </label>
  );
}
